import {
    CollectionReference,
    DocumentReference,
    FirestoreError, getFirestore,
    QueryConstraint,
    waitForPendingWrites,
} from "@firebase/firestore";
import { initializeApp, FirebaseApp, FirebaseError, getApps, getApp } from 'firebase/app';
import { indexedDBLocalPersistence, initializeAuth } from "firebase/auth";
import {
    connectAuthEmulatorIfConfigured,
    connectFirestoreEmulatorIfConfigured,
} from "./testing/firebaseEmulators";
import {
    Firestore,
    initializeFirestore,
    persistentLocalCache,
    persistentSingleTabManager,
    FirestoreSettings,
    disableNetwork,
    enableNetwork,
    Query,
    QuerySnapshot,
    onSnapshot,
    getDocsFromCache, getDocFromCache, query,
} from "firebase/firestore";
import { FirebaseStorage, getStorage } from "firebase/storage";
import { Database, getDatabase } from "@firebase/database";
import { useContext, useEffect, useMemo, useState } from "react";
import { FirebaseContext } from "./context";
import { deviceConfig } from "./deviceconfig";
import { firebaseConfig } from "./config";
import { getLogger } from "./logger";

const logger = getLogger('firebase');

// Launch-timing instrumentation. Marks are emitted only for the trip-page critical-path
// contexts so the log isn't flooded (each entry is itself an IndexedDB write, which would
// otherwise perturb the timing we're measuring). Read with millis enabled in the log window.
const perfLogger = getLogger('perf');
const PERF_CONTEXTS = new Set([
    'useTripById',
    'useTripPlacesById',
    'useUserData:private-user-data',
    // The deferred global activities listener only emits through useMemoizedQuery once it arms,
    // so the first `query useAllTripActivities` perf line marks the arming moment — used to
    // verify (on device and in the route-restoration test) that it stays off the launch path.
    'useAllTripActivities',
]);

// Global type-safe singleton for hot reload protection
type GlobalFirebaseRefs = {
    app: FirebaseApp;
    firestore: Firestore;
    storage: FirebaseStorage;
    database: Database;
    id: number;
};

declare global {
    // For Node.js and browser globalThis
    // eslint-disable-next-line no-var,vars-on-top
    var _firebaseRefs: GlobalFirebaseRefs | undefined;
    // eslint-disable-next-line no-var,vars-on-top
    var _firebaseSequenceGenerator: number | undefined;
}

export type FirebaseRefs = GlobalFirebaseRefs;

export function useFirebaseRefs() {
    const firebaseRefsFromContext = useContext(FirebaseContext);
    if (!firebaseRefsFromContext) {
        throw new Error("FirebaseContext has not been properly configured!");
    }
    return firebaseRefsFromContext;
}

export function newFirebaseRefs(reason: string): FirebaseRefs {
    // Use a global variable to persist across hot reloads
    if (globalThis._firebaseSequenceGenerator === undefined) {
        globalThis._firebaseSequenceGenerator = 0;
    }
    if (!globalThis._firebaseRefs) {
        const firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

        const firestoreSettings: FirestoreSettings = {
            // Auto-detect when the streaming (WebChannel) transport is blocked/slow and fall
            // back to long-polling. Observed locally on WebKit: Firestore queries (e.g. the
            // trips list) never resolve over WebChannel, wedging those pages; enabling
            // auto-detect makes them complete. It's a no-op where WebChannel works (it only
            // falls back when needed), so it's a low-risk resilience setting across engines.
            experimentalAutoDetectLongPolling: true,
            ...(deviceConfig.firebaseCacheSizeMB > 0
                ? { localCache: persistentLocalCache({
                    cacheSizeBytes: deviceConfig.firebaseCacheSizeMB * 1024 * 1024,
                    // Launch perf: this is a single-WebView app, so seize the IndexedDB primary
                    // lease immediately instead of waiting out a prior page's stale lease (~5s
                    // default ownership timeout). That wait was stalling the first post-auth cache
                    // read (private-user-data), which gates the Plans view on launch.
                    tabManager: persistentSingleTabManager({ forceOwnership: true }),
                }) }
                : {}),
        };
        // Always initialize Firestore with settings if needed
        const firestore = initializeFirestore(firebaseApp, firestoreSettings);

        const refs: GlobalFirebaseRefs = {
            app: firebaseApp,
            firestore,
            storage: getStorage(firebaseApp),
            database: getDatabase(firebaseApp),
            id: globalThis._firebaseSequenceGenerator++,
        };
        connectFirestoreEmulatorIfConfigured(firestore);

        globalThis._firebaseRefs = refs;
        logger.log(`Initializing Firebase ${refs.id}. Reason: ${reason}`);
    } else {
        logger.log(`Firebase already initialized. Reason: ${reason}`);
    }

    //instructions to setup firestore and cloud function emulation
    //if project is not listed by 'firebase projects:list' try logging out and back in to firebase CLI
    //'firebase init firestore' in the client directory to generate local firestore.rules for emulator
    //'npm run build' in the server/functions directory to redeploy cloud function changes in the emulator
    //'npm run start-emulator' in the client directory to run emulator including firestore and cloud functions
    //Note images still save to prod while onDeletePhoto fails while trying to delete in the storage emulator
    //scripts to seed firestore in emulator - https://medium.com/firebase-developers/seeding-firestore-data-in-emulator-c8485e797135

    //uncomment to use firestore emulator in development
    //const db = getFirestore();
    //if (process.env.NODE_ENV === "development") {
    //    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    //}

    if (!globalThis._firebaseRefs) {
        throw new Error("Failed to initialize FirebaseRefs. globalThis._firebaseRefs is undefined.");
    }
    return globalThis._firebaseRefs;
}

export function useFirebaseAuth() {
    const { app, id } = useFirebaseRefs() as FirebaseRefs;

    return useMemo(
        () => {
            logger.log(`Initializing Firebase Auth for ref id ${id}`);
            const auth = initializeAuth(app,
                { persistence: [ indexedDBLocalPersistence ] }); // Always persist authorization data
            connectAuthEmulatorIfConfigured(auth);
            return auth;
        },
        [ app, id ]
    );
}

function defaultOnSnapshotErrorHandler(context: string) {
    return (error: FirebaseError) => logger.warn(`\
An error occurred in onSnapshot(). context=${context}, \
name=${error.name}, code=${error.code}, message=${error.message}`);
}

export function pauseFirebase(
    firebaseRefs: FirebaseRefs,
    completionCallback: () => unknown,
    errorCallback: (error: any) => unknown,
) {
    let isRunning = true;
    (async () => {
        try {
            await waitForPendingWrites(firebaseRefs.firestore);
            if (isRunning) {
                // if we suspend and resume really fast, we can get into a race in which
                // the resume happens before the disableNetwork call. This avoids that.
                await disableNetwork(firebaseRefs.firestore);
                logger.log("Firestore networking paused");
                completionCallback();
            }
        } catch (err) {
            errorCallback(err);
        }
    })();
    return () => { isRunning = false; };
}

export async function resumeFirebase(firebaseRefs: FirebaseRefs) {
    const start = Date.now();
    await enableNetwork(firebaseRefs.firestore);
    logger.log(`Firestore networking resumed in ${Date.now() - start} millis`);
}

type IndexedDBUserInfo = { firebaseUserId: string | undefined, isLoading: boolean };
export function useFirebaseUserInfoFromIndexedDB(): IndexedDBUserInfo {
    const [ firebaseUserInfo, setFirebaseUserInfo ] = useState<IndexedDBUserInfo>({
        firebaseUserId: undefined,
        isLoading: true,
    });

    useEffect(() => {
        firebaseUserIdFromIndexedDB()
            .then(firebaseUserId => setFirebaseUserInfo({ firebaseUserId, isLoading: false }));
    }, []);

    return firebaseUserInfo;
}

// Upper bound on the IndexedDB peek below. This is a best-effort optimisation (it only lets us show
// cached content sooner), so giving up quickly costs nothing and never blocks the auth gate.
const INDEXEDDB_PEEK_TIMEOUT_MS = 2000;

// Digs into the Firebase Auth storage internals to obtain the Firebase ID for the current
// user. If there is no storage info in IndexedDB, this returns `undefined`.
async function firebaseUserIdFromIndexedDB(): Promise<string | undefined> {
    if (!('indexedDB' in global)) {
        return undefined;
    }

    // Do NOT call indexedDB.databases() here. It was measured to block for 200s+ on some Chrome
    // profiles, contending with Firebase Auth's own IndexedDB access and wedging the auth gate.
    // Open the auth DB directly instead; if it doesn't exist (no persisted session), the
    // upgradeneeded handler aborts so we don't create Firebase's DB.
    const [ firebaseUserId ] = await new Promise<[ string | undefined, string | undefined ]>(resolve => {
        const dbRequest = indexedDB.open('firebaseLocalStorageDb'); // We intentionally do not specify a version

        // CRITICAL: this is Firebase Auth's OWN internal database. We must close our connection
        // as soon as we're done — and release it if Firebase needs to upgrade the DB while we
        // hold it open. Leaking the connection blocks Firebase Auth's own open/upgrade of this
        // DB, which stalls session restoration for tens of seconds (onAuthStateChanged never
        // fires, so the app hangs on the auth gate, with no network activity in the meantime).
        let settled = false;
        const finish = (value: [ string | undefined, string | undefined ], db?: IDBDatabase) => {
            try { db?.close(); } catch { /* ignore */ }
            if (!settled) {
                settled = true;
                clearTimeout(timer);
                resolve(value);
            }
        };

        // A blocked open never fires success OR error -- it just sits there (another tab on this
        // origin holding the DB is enough). 'blocked' is only emitted when a version change is
        // actually pending, so it is not a reliable escape either. Time out rather than leave the
        // caller's promise, and with it the whole auth gate, permanently unresolved.
        const timer = setTimeout(() => {
            logger.warn('firebaseUserIdFromIndexedDB: timed out; proceeding without a cached uid');
            // Do NOT touch dbRequest.result here: reading it on a request that has not finished
            // throws InvalidStateError. If the open later succeeds, the success handler closes it.
            finish([ undefined, 'timeout' ]);
        }, INDEXEDDB_PEEK_TIMEOUT_MS);

        dbRequest.addEventListener('error', () => finish([ undefined, 'db-error' ]));
        dbRequest.addEventListener('blocked', () => finish([ undefined, 'blocked' ]));
        dbRequest.addEventListener('upgradeneeded', () => {
            // DB didn't exist (no persisted session). Abort so we don't create Firebase's DB.
            try { dbRequest.transaction?.abort(); } catch { /* ignore */ }
            finish([ undefined, 'no-db' ], dbRequest.result);
        });
        dbRequest.addEventListener('success', () => {
            const db = dbRequest.result;
            db.onversionchange = () => { try { db.close(); } catch { /* ignore */ } };
            try {
                const tx = db.transaction('firebaseLocalStorage', 'readonly');
                const objectStore = tx.objectStore('firebaseLocalStorage');
                const key = `firebase:authUser:${firebaseConfig.apiKey}:[DEFAULT]`;
                const recordRequest = objectStore.get(key);
                recordRequest.addEventListener('error', () => finish([ undefined, 'request-error' ], db));
                recordRequest.addEventListener('success', () => {
                    const uid = recordRequest.result?.value?.uid;
                    finish(
                        [ typeof uid === 'string' ? uid : undefined, typeof uid === 'string' ? undefined : 'uid' ],
                        db,
                    );
                });
            } catch {
                finish([ undefined, 'tx-error' ], db);
            }
        });
    });

    return firebaseUserId;
}

// Wraps a Firestore listener with self-healing recovery. Firestore tears a listener down
// permanently when its stream errors and never retries it; a common trigger is a PERMISSION_DENIED
// (or a transient WebView transport error) fired while a freshly minted auth token is still
// propagating right after login. Without recovery that left lists/docs spinning until the app was
// relaunched.
//
// `subscribe` wires up the actual onSnapshot and returns its unsubscribe. It is handed two
// callbacks it must invoke: `onHealthy` on every *server* snapshot (a confirmed-good response),
// and `onStreamError` on a stream error. Design notes:
//   - Backoff resets only via `onHealthy` (server snapshots). A from-cache snapshot must NOT reset
//     it: Firestore delivers an immediate cached snapshot before a listen error, so resetting on
//     any snapshot would zero the backoff every cycle and defeat it.
//   - We cap the retry *delay* (30s), not the number of attempts, so an arbitrarily long auth or
//     connectivity outage still recovers on its own; one retry per 30s is negligible churn.
//   - Only the first error of a failing streak is logged (reset once healthy), so a permanently
//     failing query doesn't spam the log every retry.
//   - A stream error that arrives after teardown is ignored (no retry, no log).
function subscribeWithRecovery(
    subscribe: (onHealthy: () => void, onStreamError: (error: FirestoreError) => void) => () => void,
    onError: (error: FirestoreError) => void,
): () => void {
    const MAX_RETRY_DELAY_MS = 30000;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryAttempt = 0;
    let loggedThisStreak = false;

    const connect = () => {
        unsubscribe = subscribe(
            () => { // onHealthy: a server snapshot arrived, so the stream is good again
                retryAttempt = 0;
                loggedThisStreak = false;
            },
            error => {
                if (cancelled) {
                    return;
                }
                if (!loggedThisStreak) {
                    onError(error); // log only the first failure of a streak
                    loggedThisStreak = true;
                }
                const delay = Math.min(MAX_RETRY_DELAY_MS, 500 * 2 ** retryAttempt);
                retryAttempt += 1;
                retryTimer = setTimeout(() => {
                    if (!cancelled) {
                        connect();
                    }
                }, delay);
            }
        );
    };
    connect();

    return () => {
        cancelled = true;
        if (retryTimer !== undefined) {
            clearTimeout(retryTimer);
        }
        unsubscribe?.();
    };
}

// This issues a query directly against cache only, and concurrently sets up a snapshot
// subscription. The snapshot query will eventually hit cache, but it appears to do some
// waiting first; the initial cache-only query will typically resolve much earlier.
//
// If the first proper onSnapshot response comes in before the cache response, the cache
// response will be discarded.
function onCacheFirstSnapshot<T>(
    q: Query<T>,
    onNext: (snapshot: QuerySnapshot<T>, type: 'initial-cache' | 'cache' | 'server') => void,
    onError: (error: FirestoreError) => void
) {
    let hasReceivedResponse = false;

    getDocsFromCache(q)
        .then(snapshot => {
            if (!hasReceivedResponse) {
                onNext(snapshot, 'initial-cache');
            }
        })
        .catch(onError);

    return subscribeWithRecovery(
        (onHealthy, onStreamError) => onSnapshot( // eslint-disable-line no-restricted-syntax
            q,
            snapshot => {
                hasReceivedResponse = true;
                const fromCache = snapshot.metadata.fromCache;
                if (!fromCache) {
                    onHealthy(); // a server response resets the recovery backoff
                }
                onNext(snapshot, fromCache ? 'cache' : 'server');
            },
            onStreamError,
        ),
        onError,
    );
}

// It is important to memoize or other stabilize the query argument provided; otherwise, this
// can easily devolve into an infinite render-loop
export function useMemoizedQuery<T>(
    q: Query<T> | undefined,
    context: string
): T[] | undefined {
    const [ results, setResults ] = useState<T[] | undefined>();

    useEffect(() => {
        if (!q) {
            setResults(undefined);
            return () => { };
        }

        return onCacheFirstSnapshot(
            q,
            (snapshot, type) => {
                if (PERF_CONTEXTS.has(context)) {
                    perfLogger.log(`query ${context} ${type} size=${snapshot.size} fromCache=${snapshot.metadata.fromCache}`);
                }
                // Only the pre-listener cache read is treated as non-authoritative when empty; a
                // live listener event — even a from-cache empty one — resolves immediately
                // (including to []). We previously tried holding the loading state on empty cache
                // events until the server confirmed (to avoid flashing empty states on a cold
                // cache), but on WebKit the first server confirmation can lag for tens of seconds
                // (WebChannel wedging + long-poll autodetect delay), which left empty lists stuck
                // on a loader: CI runs with the wider suppression timed out (#827/#839) while
                // these semantics pass. The cold-cache empty-state flash is the accepted trade.
                if (type === 'initial-cache' && snapshot.size === 0) {
                    setResults(undefined);
                } else {
                    setResults(snapshot.docs.map(doc => doc.data()));
                }
            },
            defaultOnSnapshotErrorHandler(context)
        );
    }, [ q, context ]);

    return results;
}

export function useCollection<T>(
    collection: CollectionReference<T> | undefined,
    context: string
): T[] | undefined {
    const [ results, setResults ] = useState<T[] | undefined>();

    const [ stabilizedCollection, setStabilizedCollection ] = useState(collection);

    useEffect(() => {
        if (collection) {
            setStabilizedCollection(original => {
                if (original
                    && original.path === collection.path
                    && original.id === collection.id
                    && original.firestore === collection.firestore
                ) {
                    return original;
                } else {
                    return collection;
                }
            });
        } else {
            setStabilizedCollection(undefined);
        }
    }, [ collection ]);

    return useMemoizedQuery(stabilizedCollection, context);
}

export function useDoc<T>(
    ref: DocumentReference<T> | undefined,
    context: string,
): { result: T | undefined, isLoading: boolean } {
    const [ result, setResult ]
        = useState<{ result: T | undefined, isLoading: boolean }>({ result: undefined, isLoading: true });

    const [ stabilizedRef, setStabilizedRef ] = useState(ref);

    useEffect(() => {
        if (ref) {
            setStabilizedRef(original => {
                if (original && original.path === ref.path && original.firestore === ref.firestore) {
                    return original;
                } else {
                    return ref;
                }
            });
        } else {
            setStabilizedRef(undefined);
        }
    }, [ ref ]);

    useEffect(() => {
        if (!stabilizedRef) {
            setResult({ result: undefined, isLoading: true });
            return () => { };
        } else {
            if (PERF_CONTEXTS.has(context)) {
                perfLogger.log(`doc ${context} cache-issued`);
            }
            getDocFromCache(stabilizedRef)
                .then(doc => {
                    if (PERF_CONTEXTS.has(context)) {
                        perfLogger.log(`doc ${context} cache-resolved exists=${doc.exists()}`);
                    }
                    setResult(current => {
                        if (current?.isLoading) {
                            return { result: doc.data(), isLoading: false }; // TODO should we include source metadata?
                        } else {
                            return current;
                        }
                    });
                })
                .catch(() => {
                    if (PERF_CONTEXTS.has(context)) {
                        perfLogger.log(`doc ${context} cache-miss`);
                    }
                }); // we do nothing -- wait for the proper DB query

            // Same self-healing recovery as the query path: a doc listener killed by the
            // post-login auth-token race (a terminal PERMISSION_DENIED) would otherwise stay stuck
            // at isLoading=true until the component remounts. Re-subscribe until a server snapshot
            // arrives.
            return subscribeWithRecovery(
                (onHealthy, onStreamError) => onSnapshot( // eslint-disable-line no-restricted-syntax
                stabilizedRef,
                snapshot => {
                    if (PERF_CONTEXTS.has(context)) {
                        perfLogger.log(`doc ${context} snapshot ${snapshot.metadata.fromCache ? 'cache' : 'server'} exists=${snapshot.exists()}`);
                    }
                    if (!snapshot.metadata.fromCache) {
                        onHealthy(); // a server response resets the recovery backoff
                    }
                    // A document listener fires an immediate event from the local cache. When the
                    // doc isn't cached — common on Safari/WebKit, where Firestore's IndexedDB
                    // persistence is unreliable/partitioned, so the cache is effectively cold on
                    // every navigation — that first event is `exists=false, fromCache=true`: a
                    // "not known yet" answer, not a confirmed absence. Resolving it as loaded
                    // (result: undefined, isLoading: false) made callers that redirect on a missing
                    // doc (e.g. the guide page's notFound → router.replace('/')) bounce Home before
                    // the server ever responded. Hold the loading state until either data arrives or
                    // the server confirms the absence (mirrors the empty-initial-cache guard in
                    // onCacheFirstSnapshot for queries).
                    //
                    // Trade-off: when the device is offline and the doc genuinely does not exist,
                    // no server event ever arrives, so isLoading stays true and such callers show a
                    // loader rather than resolving to not-found. That edge is preferable to a
                    // confirmed page (guide/home) flickering away on every cold-cache navigation.
                    if (snapshot.metadata.fromCache && !snapshot.exists()) {
                        return;
                    }
                    setResult({ result: snapshot.data(), isLoading: false }); // TODO should we include source metadata?
                },
                onStreamError,
                ),
                defaultOnSnapshotErrorHandler(context),
            );
        }
    }, [ stabilizedRef, context ]);

    return result;
}
